import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, ArrowRight } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { subscribeNewsletter } from "@/lib/api";
import { toast } from "sonner";

export default function Footer() {
  const { lang, t } = useLang();
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !email.includes("@")) {
      toast.error(t.common.newsletterError);
      return;
    }
    setSubmitting(true);
    try {
      await subscribeNewsletter({ email, locale: lang });
      toast.success(t.common.newsletterSuccess);
      setEmail("");
    } catch (err) {
      toast.error(t.common.newsletterError);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <footer data-testid="footer" className="bg-maly-navy text-maly-cream relative overflow-hidden">
      {/* Newsletter block */}
      <div className="border-b border-maly-cream/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 lg:py-28 grid lg:grid-cols-2 gap-12 lg:gap-24 items-end">
          <div>
            <div className="font-body text-[11px] tracking-[0.3em] uppercase opacity-60 mb-6">
              MALY · Newsletter
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
              {t.footer.newsletterHead}
            </h2>
            <p className="font-body text-base lg:text-lg opacity-70 mt-6 max-w-md leading-relaxed">
              {t.footer.newsletterSub}
            </p>
          </div>

          <form
            data-testid="newsletter-form"
            onSubmit={handleSubmit}
            className="flex items-end border-b border-maly-cream/30 pb-3 gap-4"
          >
            <input
              type="email"
              required
              data-testid="newsletter-email-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t.common.newsletterPlaceholder}
              className="flex-1 bg-transparent border-none outline-none font-body text-base lg:text-lg placeholder:text-maly-cream/40 text-maly-cream"
            />
            <button
              type="submit"
              data-testid="newsletter-submit"
              disabled={submitting}
              className="font-body text-[11px] tracking-[0.25em] uppercase font-medium flex items-center gap-2 disabled:opacity-50 hover:opacity-70 transition-opacity"
            >
              {submitting ? t.common.sending : t.common.newsletterSubscribe}
              <ArrowRight size={14} />
            </button>
          </form>
        </div>
      </div>

      {/* Main footer */}
      <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-20 grid grid-cols-2 lg:grid-cols-5 gap-12">
        <div className="col-span-2">
          <div className="font-display text-3xl tracking-[0.35em] mb-6">MALY</div>
          <p className="font-body text-sm opacity-60 leading-relaxed max-w-xs">
            {lang === "en"
              ? "Luxury performance fashion for the modern woman of the course."
              : "Luxus Performance-Mode für die moderne Frau des Platzes."}
          </p>
          <div className="flex items-center gap-5 mt-8">
            <a href="https://instagram.com" target="_blank" rel="noreferrer" aria-label="Instagram" data-testid="footer-instagram">
              <Instagram size={18} strokeWidth={1.4} className="opacity-70 hover:opacity-100 transition-opacity" />
            </a>
          </div>
        </div>

        <div>
          <div className="font-body text-[10px] tracking-[0.3em] uppercase opacity-50 mb-5">{t.footer.explore}</div>
          <ul className="space-y-3 font-body text-sm">
            <li><Link to="/shop" className="opacity-80 hover:opacity-100 link-underline">{t.nav.shop}</Link></li>
            <li><Link to="/lookbook" className="opacity-80 hover:opacity-100 link-underline">{t.nav.lookbook}</Link></li>
            <li><Link to="/about" className="opacity-80 hover:opacity-100 link-underline">{t.nav.about}</Link></li>
            <li><Link to="/journal" className="opacity-80 hover:opacity-100 link-underline">{t.nav.journal}</Link></li>
            <li><Link to="/community" className="opacity-80 hover:opacity-100 link-underline">{t.nav.community}</Link></li>
          </ul>
        </div>

        <div>
          <div className="font-body text-[10px] tracking-[0.3em] uppercase opacity-50 mb-5">{t.footer.help}</div>
          <ul className="space-y-3 font-body text-sm">
            <li><Link to="/contact" className="opacity-80 hover:opacity-100 link-underline">{t.footer.contact}</Link></li>
            <li><span className="opacity-80">{t.footer.shipping}</span></li>
            <li><span className="opacity-80">{t.footer.returns}</span></li>
          </ul>
        </div>

        <div>
          <div className="font-body text-[10px] tracking-[0.3em] uppercase opacity-50 mb-5">{t.footer.legal}</div>
          <ul className="space-y-3 font-body text-sm">
            <li><span className="opacity-80">{t.footer.privacy}</span></li>
            <li><span className="opacity-80">{t.footer.terms}</span></li>
            <li><span className="opacity-80">{t.footer.imprint}</span></li>
          </ul>
        </div>
      </div>

      <div className="border-t border-maly-cream/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 font-body text-[11px] tracking-[0.15em] uppercase opacity-50">
          <span>{t.footer.rights}</span>
          <span>{t.footer.paymentSecure}</span>
        </div>
      </div>
    </footer>
  );
}
